import { useEffect } from "react";

import Head from "next/head";
import { useRouter } from "next/router";
import { destroyCookie, parseCookies, setCookie } from "nookies";

import { ModalProvider } from "@/components/Modal/contexts/ModalContext";
import useLastQrConfirmation from "@/hooks/useLastQrConfirmation";
import useTranslation from "@/hooks/useTranslation";
import IQRCodeResult from "@/interfaces/IQRCodeResult";
import { FlushCookies, ToggleTabletEnvironment } from "@/utils/ReactNativeUtils";
import QRScannerPage from "@/views/QRScannerPage";

export default function Scanner() {
  const { replace: replaceRoute } = useRouter();
  const { translation } = useTranslation();
  const [, setLastQrConfirmation] = useLastQrConfirmation();

  useEffect(() => {
    ToggleTabletEnvironment(false);
  }, []);

  async function handleQrCode(data: IQRCodeResult, result: string) {
    Object.keys(parseCookies()).forEach((cookieName) => {
      destroyCookie(null, cookieName);
    });

    if ("serviceWorker" in navigator) {
      await Promise.all([
        caches.delete("start-url"),
        caches.delete("defaults"),
        caches.delete("next-data"),
      ]);
    }

    localStorage.clear();
    sessionStorage.clear();
    setLastQrConfirmation(Date.now());

    setCookie(
      null,
      "session",
      JSON.stringify({
        qrCode: result,
        companyId: data.id_company,
        isTablet: data.is_tablet,
        tableNumber: data.command_number ? 0 : data.table_number,
        commandNumber: data.command_number,
      }),
      {
        sameSite: "Strict",
        maxAge: (data.is_tablet ? 365 : 3) * 24 * 60 * 60,
        path: "/",
      }
    );

    setCookie(null, "animAndEffects", JSON.stringify(true), {
      sameSite: "Strict",
      maxAge: 365 * 24 * 60 * 60,
      path: "/",
    });

    await FlushCookies();
    await replaceRoute("/");
  }

  return (
    <>
      <Head>
        <title>{`${translation.qrScanner.scanner} – ${translation.name}`}</title>
        <meta name="description" content={translation.description} />
      </Head>
      <ModalProvider>
        <QRScannerPage onScan={handleQrCode} />
      </ModalProvider>
    </>
  );
}
